import { DOCUMENT, NgTemplateOutlet, NgClass, isPlatformServer } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, Subscription } from 'rxjs';

import {
  AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostBinding,
  Inject, input, OnDestroy, OnInit, output, PLATFORM_ID, Renderer2, signal, TemplateRef, viewChild, viewChildren
} from '@angular/core';

import { slideInOutHorizontallyAnimation } from '../../_animations/slide-in-out.animation';
import { expandCollapseAnimation } from '../../_animations/expand-collapse.animation';

import { IHeaderIconLink, ILanguageSelectorData } from '../../_interfaces/header.interface';
import { IPushCard, IPushCardWithLayout } from '../../_interfaces/push-card.interface';
import { IMenu, INavigationNode } from '../../_interfaces/menu.interface';

import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { ImageRatioEnum } from '../../_enums/image-ratio.enum';
import { PushSizeEnum } from '../push-card/push-card.enum';
import { PositionEnum } from '../../_enums/position.enum';
import { IconSizeEnum } from '../../_enums/icon.enum';

import { DeviceService } from '../../_services/device.service';

import { SdLanguageSelectorComponent } from '../language-selector/language-selector.component';
import { SdButtonIconComponent } from '../buttons/button-icon/button-icon.component';
import { SdPushCardComponent } from '../push-card/push-card.component';
import { SdBannerComponent } from '../medias/banner/banner.component';
import { SdIconComponent } from '../icons/icon/icon.component';
import { SdLinkComponent } from '../links/link/link.component';
import { SdListComponent } from '../lists/list/list.component';
import { SdHtmlComponent } from '../html/html.component';

import { SdLibConfig } from '../../sd-lib-config';

@Component({
  selector: 'sd-menu',
  templateUrl: './menu.component.html',
  animations: [
    expandCollapseAnimation({
      anchor: 'expandCollapse',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0
    }),
    slideInOutHorizontallyAnimation({
      anchor: 'slideInOutHorizontally',
      durationIn: 250,
      delayIn: 0,
      durationOut: 250,
      delayOut: 0,
      translate: '150%'
    })
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    SdIconComponent,
    SdBannerComponent,
    SdLinkComponent,
    NgTemplateOutlet,
    SdListComponent,
    SdButtonIconComponent,
    SdPushCardComponent,
    SdLanguageSelectorComponent,
    TranslateModule,
    SdHtmlComponent
  ]
})
export class SdMenuComponent implements AfterViewChecked, AfterViewInit, OnDestroy, OnInit {
  themesConfig = input<ThemesEnum.light | ThemesEnum.dark | BrandThemesEnum.brand>(ThemesEnum.light);
  pushTemplate = input<TemplateRef<SdPushCardComponent>>();
  languageSelectorData = input<ILanguageSelectorData>();
  iconLinks = input<IHeaderIconLink[]>();
  activeCatId = input<string>();
  menuData = input<IMenu>();
  alpha = input(false);
  class = input('');

  pushCardClicked = output<IPushCard | IPushCardWithLayout>();
  clickCardFrames = output<IPushCard | IPushCardWithLayout>();
  languageSelected = output<string>();
  clickNavigation = output<string>();
  isMenuOpen = output<boolean>();
  linkClicked = output<any>();

  readonly mainNavigationItem = viewChildren<ElementRef<HTMLElement>>('mainNavigationItem');
  readonly mainNavigationList = viewChild<ElementRef<HTMLElement>>('mainNavigationList');
  readonly languageSelector = viewChild<ElementRef<HTMLElement>>('languageSelector');
  readonly mobileMenuButton = viewChild<ElementRef<HTMLElement>>('mobileMenuButton');
  readonly activeBar = viewChild<ElementRef<HTMLElement>>('activeBar');

  @HostBinding('class')
  get hostClasses(): string {
    return this.computedHostClasses();
  }

  private computedHostClasses = computed(() => {
    const theme = this.themesConfig();
    return [
      this.class(),
      this.alpha() ? 'is-alpha' : '',
      (theme === BrandThemesEnum.brand) ? ('is-theme-' + ThemesEnum.light) : theme ? ('is-theme-' + theme) : ''
    ]
      .filter(Boolean)
      .join(' ');
  });

  PushSizeEnum = PushSizeEnum;
  ImgRatio = ImageRatioEnum;
  IconSize = IconSizeEnum;

  bodyChildren!: HTMLCollection;
  textConfig: PositionEnum;
  isSSR!: boolean;

  subscriptions = new Subscription();

  openedUidLevel1 = signal<string>('');
  openedUidLevel2 = signal<string>('');
  openedMobile = signal(false);
  isDesktop = signal(true);
  isMobile = signal(false);
  menuItems = signal<INavigationNode[]>([]);
  activeId = signal<string>(undefined);
  subcategoryActiveId = signal<string>(undefined);

  constructor(
    private readonly renderer: Renderer2,
    private readonly translateService: TranslateService,
    private deviceService: DeviceService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) readonly platformId: any,
    readonly sdLibConfig: SdLibConfig
  ) {
    this.textConfig = sdLibConfig.get().headerLayout;
    this.isSSR = isPlatformServer(platformId);

    effect(() => {
      const navNode = this.menuData()?.navigationNode;

      this.menuItems.set((navNode?.uid && navNode?.children) ? navNode.children.map((level1) => ({
        ...level1,
        numberOfBrandCat: this.getBrandCat(level1.children),
        brandCornerPush: this.getBrandCornerPush(level1.others?.pushs),
        hasLevel3: level1.children?.some((level2) => !!level2.children)
      })) : []);

      this.activeId.set(this.activeCatId());
      this.manageActiveCat();
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.deviceService.isMobile,
        this.deviceService.isDesktop,
        this.deviceService.isDesktopLarge
      ]).subscribe(([mobile, desktop, large]) => {
        this.isDesktop.set(desktop || large);
        this.isMobile.set(mobile);
        this.updateState();
      })
    );

    this.bodyChildren = this.document.body.children;
  }

  ngAfterViewInit(): void {
    this.updateState();
  }

  ngAfterViewChecked() {
    this.manageActiveCat();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleMobileMenu(): void {
    const openedMobile = !this.openedMobile();

    this.isMenuOpen.emit(openedMobile);
    this.openedMobile.set(openedMobile);

    this.openedUidLevel1.set(undefined);
    this.openedUidLevel2.set(undefined);

    /* A11y - If mobile menu is open, set focus on the first element and aria-hidden all contents */
    setTimeout(() => {
      if (this.openedMobile()) {
        const el = this.mainNavigationList()?.nativeElement?.firstElementChild.querySelector('.c__nav-link');
        if (el) {
          (el as HTMLElement).focus();
        }

        for (const child of this.bodyChildren) {
          if (child.className !== 'cdk-overlay-container') {
            child.setAttribute('aria-hidden', 'true');
          }
        }
      } else {
        for (const child of this.bodyChildren) {
          child.removeAttribute('aria-hidden');
        }
      }
    }, 250);
  }

  openMenuLevel(event: Event, catUid: string, level: number, link: string, index?: number): void {
    const activeBar = this.activeBar().nativeElement;

    this.renderer.setStyle(activeBar, 'opacity', 1);

    const selectedMenu = this.mainNavigationItem()?.[index]?.nativeElement;
    if (selectedMenu) {
      this.activeBarPosition(selectedMenu.offsetWidth, selectedMenu.offsetLeft);
    }

    if (level === 1) {
      let opened = this.openedUidLevel1();

      const isA11yClick = (event instanceof PointerEvent) &&
        event.pointerType !== 'mouse' &&
        event.pointerType !== 'touch';

      if (this.isDesktop() && !isA11yClick) {
        this.isMenuOpen.emit(true);
        this.openedUidLevel1.set(catUid);
      } else {
        if (isA11yClick) {
          this.isMenuOpen.emit(opened !== catUid);
        }
        this.openedUidLevel1.set(opened === catUid ? undefined : catUid);
      }

      opened = this.openedUidLevel1();

      if (opened !== catUid) {
        this.renderer.setStyle(activeBar, 'opacity', 0);
      }

      if (opened) {
        this.clickNavigation.emit(link);
      }

      this.subcategoryActiveId.set(undefined);
      this.openedUidLevel2.set(undefined);
      this.activeId.set(opened);

      /* A11y - Focus inside mobile menu */
      if (!this.isDesktop()) {
        setTimeout(() => {
          const parent = (event.target as HTMLElement).parentElement;
          const firstElementFocusable = parent.querySelector('.c__nav-list')?.firstElementChild.querySelector('.c__nav-link');

          if (this.openedUidLevel1()) {
            (firstElementFocusable as HTMLElement).focus();
          }
        }, 250);
      }
    } else if (level === 2) {
      const opened = this.openedUidLevel2() === catUid;
      if (!opened) {
        this.clickNavigation.emit(link);
      }
      this.subcategoryActiveId.set(catUid);
      this.openedUidLevel2.set(opened ? undefined : catUid);
    }
  }

  closeMenu(): void {
    if (this.openedUidLevel1() || this.openedUidLevel2() || this.openedMobile()) {
      this.subcategoryActiveId.set(undefined);
      this.activeId.set(this.activeCatId());
      this.openedUidLevel1.set(undefined);
      this.openedUidLevel2.set(undefined);
      this.openedMobile.set(false);
      this.isMenuOpen.emit(false);
      this.isMobile.set(false);

      const activeId = this.activeId();

      if (activeId) {
        this.activeBarSetForActiveCat(activeId);
      } else {
        this.renderer.setStyle(this.activeBar().nativeElement, 'opacity', 0);
      }
    }
  }

  changeLanguage(value: string): void {
    this.languageSelected.emit(value);
    this.closeMenu();
  }

  onFocusGuardModal(value: string): void {
    /* A11y - Focus inside mobile menu */
    if (!this.isDesktop()) {
      if (value === 'last') {
        this.mobileMenuButton()?.nativeElement.focus();
      } else {
        this.languageSelector().nativeElement.querySelector('button').focus();
      }
    }
  }

  onPushCardClicked(event: IPushCard | IPushCardWithLayout): void {
    this.pushCardClicked.emit(event);
  }

  onClickCardFrames(event: IPushCard | IPushCardWithLayout): void {
    this.clickCardFrames.emit(event);
  }

  onLinkClick(
    url: string,
    level: number,
    navLevel?: string,
    category?,
    translateStr?: string,
    isBrandCorner?: boolean,
    brand?: string
  ) {
    this.closeMenu();
    this.clickNavigation.emit(navLevel);

    let _category: string = translateStr?.length > 0
      ? this.translateService.instant(translateStr) + ' ' + category?.title
      : category?.title;

    if (brand) {
      _category += ' ' + this.translateService.instant('navigation.menu.byBrand', { brand });
      _category = _category.replace('<span class=\'is-capitalized\'>', '').replace('</span>', '');
    }

    this.linkClicked.emit({
      url,
      level,
      category: _category,
      isBrandCorner: !!isBrandCorner
    });
  }

  private manageActiveCat(): void {
    if (!this.isSSR && this.isDesktop()) {
      const activeId = this.activeCatId();
      if (activeId) {
        this.activeBarSetForActiveCat(activeId);
      } else {
        this.renderer.setStyle(this.activeBar().nativeElement, 'opacity', 0);
      }
    }
  }

  private activeBarSetForActiveCat(activeId: string): void {
    const active = this.mainNavigationItem().filter((el) => el.nativeElement.id === activeId)[0]?.nativeElement;
    if (active) {
      this.activeBarPosition(active.offsetWidth, active.offsetLeft);
    }
  }

  private activeBarPosition(width: number, posX: number): void {
    const activeBar = this.activeBar().nativeElement;

    this.renderer.setStyle(activeBar, 'width', width + 'px');
    this.renderer.setStyle(activeBar, 'left', posX + 'px');
    this.renderer.setStyle(activeBar, 'opacity', 1);
  }

  private updateState(): void {
    this.openedMobile.set(false);
    this.openedUidLevel1.set(undefined);
    this.openedUidLevel2.set(undefined);
  }

  private getBrandCat(subcategories: INavigationNode[]): number {
    return (subcategories ?? []).reduce((count, { brand }) => brand ? count + 1 : count, 0);
  }

  private getBrandCornerPush(pushs?: IPushCardWithLayout[]): IPushCardWithLayout {
    return pushs?.find(({ brand }) => !!brand);
  }
}
